import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ReceivableDomain } from './domain/receivable-domain';
import { map, Observable, of } from 'rxjs';
import { environment } from '../environments/environment';
import { PaymentParameters } from './ebiz/domain/payment-parameters';
import { PaymentTransaction } from './ebiz/domain/payment-transaction';
import { Account, StringResponse } from '@ohioepa/oepa-ebiz-lib';
import { Parameter } from './ebiz/domain/parameter';
import { OepaAnonymousTokenService } from '@ohioepa/oepa-anonymous-auth-lib';
import { OepaIopAuthLibService } from '@ohioepa/oepa-iop-auth-lib';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private rootURL = environment.ebizRootURL;
  private account: Account | undefined;
  private paymentTransaction: PaymentTransaction | undefined;
  private paymentParameters: PaymentParameters | undefined;
  private testAccount : boolean = false;
  private authToken: string = '';
  private anonymousParams: any = {};
  private ebizVersion: string = '';

  constructor(private http: HttpClient, private tokenService: OepaAnonymousTokenService, 
    private authService: OepaIopAuthLibService ) { }
  
  public getPaymentTransaction(): PaymentTransaction | undefined {
    return this.paymentTransaction;
  }

  public setPaymentTransaction(paymentTransaction: PaymentTransaction) {
    this.paymentTransaction = paymentTransaction;
  }

  public getPaymentParameters(): PaymentParameters | undefined {
    return this.paymentParameters;
  }

  public setPaymentParameters(paymentParameters: PaymentParameters) {
    this.paymentParameters = paymentParameters;
  }

  public submitReceivablesForPayment(receivableList: ReceivableDomain[]) : Observable<PaymentTransaction> {
    let serviceURL = '/ebiz/api/v1/online-payments/receivables-payment/submit';
    const url = this.getURLWithAccountInfo(serviceURL);
    this.getTokenInfo();
    return this.http.post<PaymentTransaction>(this.rootURL + url, receivableList, { 
      headers: { 'Authorization': 'Bearer ' + this.authToken },
      params: this.anonymousParams
    });
  }

  public retrieveInitialPaymentParametersForTransaction(paymentTransaction: PaymentTransaction) : Observable<PaymentParameters> {
    const serviceURL = '/ebiz/api/v1/online-payments/payment-parameters/initial';
    const url = this.getURLWithAccountInfo(serviceURL);
    this.getTokenInfo();
    return this.http.post<PaymentParameters>(this.rootURL + url, paymentTransaction, { 
      headers: { 'Authorization': 'Bearer ' + this.authToken },
      params: this.anonymousParams
    });
  }

  public retrieveFinalPaymentParametersForTransaction(paymentTransaction: PaymentTransaction, paymentTypeCode: string) : Observable<PaymentParameters> {
    const serviceURL = '/ebiz/api/v1/online-payments/payment-parameters/final/payment-type/' + paymentTypeCode;
    const url = this.getURLWithAccountInfo(serviceURL);
    //, JSON.stringify(paymentTransaction)
    this.getTokenInfo();
    return this.http.post<PaymentParameters>(this.rootURL + url, paymentTransaction, { 
      headers: { 'Authorization': 'Bearer ' + this.authToken },
      params: this.anonymousParams
    });
  }

  private getURLWithAccountInfo(serviceURL: string) : string {
    if (this.account && this.account.userID && !this.testAccount) {
      serviceURL = serviceURL + "?userid=" + this.account?.userID;
    }
    return serviceURL;
  }

  public retrieveAccountByUserID(userID: string) : Observable<Account> {
    const serviceURL = '/ebiz/api/v1/online-payments/accounts/' + userID //+ '/' + testAccount;
    const url = this.getURLWithAccountInfo(serviceURL);
    let token = this.authService.getAccessToken(); 
    return this.http.get<Account>(this.rootURL + url, { 
      headers: { 'Authorization': 'Bearer ' + token },
      params: this.anonymousParams
    }).pipe(map((account: Account) => {
      this.account = account;
      return account;
    }));
  }

  public getAccount() : Account | undefined {
    return this.account;
  }

  /*
  public getServiceFeePct() : Observable<Parameter> {
    const serviceURL = '/ebiz/api/v1/parameters/names/PAYMENT_CC_SERVICE_FEE/';
    const url = this.getURLWithAccountInfo(serviceURL);
    const token = this.authService.getIdToken();
    //const token = this.tokenService.encode(this.authService.getIdToken());
    return this.http.get<Parameter>(this.rootURL + url,  { headers: {'Authorization': 'Bearer ' + token}})
  }
*/
  public getServiceFeePct() : Observable<Parameter> {
    const serviceURL = '/ebiz/api/v1/online-payments/serviceFeePct/PAYMENT_CC_SERVICE_FEE/';
    const url = this.getURLWithAccountInfo(serviceURL); 
    this.getTokenInfo();
    return this.http.get<Parameter>(this.rootURL + url, { 
      headers: { 'Authorization': 'Bearer ' + this.authToken },
      params: this.anonymousParams
    })
  }

  public checkPaymentUnlocked(revenueID: string) : Observable<boolean> {
    const serviceURL = '/ebiz/api/v1/online-payments/unlocked/revenueID/' + revenueID;
    const url = this.getURLWithAccountInfo(serviceURL);
    this.getTokenInfo();
    return this.http.get<boolean>(this.rootURL + url, { 
      headers: { 'Authorization': 'Bearer ' + this.authToken },
      params: this.anonymousParams
    });
  }

  
  public verifyUserAccount(eidmuserid : string, userID : string) : Observable<boolean> {
    const serviceURL = '/ebiz/api/v1/accounts/' + eidmuserid +  '/' + userID;
    return this.http.get<boolean>(this.rootURL + serviceURL);
  }

  public setTestAccount(isTesting : boolean){
    this.testAccount = isTesting;
  }
  public isTestAccount() : boolean{
    return this.testAccount;
  }

  public validateRecaptcha(token: string | null) : Observable<any> {
    const payload = {
        event: {
          token: token,
          siteKey: environment.recaptchaSiteKey
      }
    }
    const url = 'https://recaptchaenterprise.googleapis.com/v1/projects/ohioepaguestpay-1739459902242/assessments?key=' + environment.recaptchaAPIKey;
    return this.http.post(url, payload);
  }

  private getTokenInfo() {
    if (this.account && this.account.userID && !this.testAccount){
      this.authToken = this.authService.getAccessToken();
      this.anonymousParams = {};
    } else {
      this.loadAnonymousToken();
    }
  }

  private loadAnonymousToken() {
    const tokenInfo = this.tokenService.getTokenInfo();
    this.authToken = tokenInfo.authToken;
    this.anonymousParams = { oepats: tokenInfo.timestamp, oepase: tokenInfo.seed };
  }

  public getEBizVersion(): Observable<string> {
    if (this.ebizVersion) {
      return of(this.ebizVersion);
    } else {
      const url = this.rootURL + '/api/v1/gateway/version';
      return this.http.get<StringResponse>(url, { params: { 'app': 'ONLINE_PAYMENTS' } }).pipe(map((version: StringResponse) => {
        if (version && version.response) {
          this.ebizVersion = version.response;
        } else {
          this.ebizVersion = 'unknown';
        }
        return this.ebizVersion;
      }));
    }
  }
}
